/* behaviorTrend.ts — 행동 개선 추이의 **순수 로직** (설계 docs/specs/2026-08-03-review-enrichment-design.md §5)
 *
 * 왜 UI와 분리하나: §5의 "오독 방지 3종"은 전부 **판정 규칙**이라 눈으로 못 잡는다.
 *   - 좋은 방향이 지표마다 반대다(churn만 높아야 좋다) → 색을 잘못 칠하면 개선을 악화로 보여준다
 *   - value=null을 0으로 보간하면 "개선한 것처럼" 보인다
 *   이 두 개는 렌더링 코드에 섞어두면 회귀를 못 막는다 → 여기 모아 단위테스트로 못 박는다.
 *
 * 주 지표는 승률이 아니라 **행동 9종**이다(§5 원칙) — 승률은 시장이 좌우해 복기 효과로 귀속하면 과장이다.
 */

// 판정·단위·라벨 3종은 서버 알림과 함께 쓰는 코어 정본으로 옮겼다(2026-09-27) — 기존 import 경로 호환을 위해 다시 내보낸다.
import { judgeDelta, fmtValue, type Delta } from '@core/sim/behavior/behaviorTrendCore';
export { judgeDelta, fmtValue, shortLabel, type Delta } from '@core/sim/behavior/behaviorTrendCore';

/** 서버 `stats.behaviorMetrics` 1건 (= behaviorEngine.BehaviorStatsMetric). */
export type TrendMetric = {
  key: string;
  label: string;
  /** null = 측정 불가(표본 미달 등). **0과 반드시 구분된다.** */
  value: number | null;
  threshold: number;
  exceeded: boolean;
  /** 'higher' = 값이 클수록 건전(회전문의 평균 보유일). 좋은 방향의 단일 출처. */
  direction: 'lower' | 'higher';
  sample?: { have: number; need: number };
  pnl?: { sum: number | null; n: number };
};

/** 복기 1회차 스냅샷. */
export type TrendRound = {
  id: string;
  created_at: string;
  mode?: string;
  /** 그 회차의 측정 대상 **체결** 건수. null = 옛 회차(필드 신설 전)라 알 수 없음. */
  tradeCount: number | null;
  metrics: TrendMetric[];
  /**
   * true = 복기 당시 측정값이 아니라 **매매 기록으로 사후 재구성한 값**.
   * 사후 계산을 "그때 재어둔 값"처럼 보여주면 안 된다 → 화면이 이 표식을 안내로 바꿔 말한다.
   */
  backfilled?: boolean;
};

/** 화면 형태 — 회차 수에 따라 자란다(§5). 첫 사용자가 빈 그래프를 보는 것만큼 나쁜 게 없다. */
export type TrendShape = 'empty' | 'gauge' | 'slope' | 'spark';

export function trendShape(rounds: number): TrendShape {
  if (rounds <= 0) return 'empty';
  if (rounds === 1) return 'gauge';   // 비교 대상이 없다 → value/threshold 게이지
  if (rounds <= 3) return 'slope';    // 첫 복기 → 최근. "나아졌다"를 가장 빨리 읽히게
  return 'spark';                     // 4+ → 추세와 일시적 튐을 구분
}

/** 개선/악화 색 — cs-tokens.css 정의 토큰만 쓴다.
 *  ⚠ rise/fall(빨강·파랑)은 **금지**: 한국 증시 관례로 "올랐다/내렸다"로 읽혀
 *    "물타기가 줄었다(개선)"가 파란 하락으로 보인다. income(그린)/alert(오렌지)가 의미 색이다. */
export function deltaColor(d: Delta): string {
  if (d === 'improved') return 'var(--cs-income)';
  if (d === 'worsened') return 'var(--cs-alert)';
  // 보합·판정 불가(unknown) 모두 mute — 이 색은 값 글자에도 쓰여서 mute-2(placeholder용·카드 위 2.19:1)는 쓸 수 없다(2026-09-13)
  return 'var(--cs-mute)';
}

/**
 * 색·문구용 톤 — `flat`(변화 없음)을 **초과 여부로 갈라준다**.
 *
 * 🔴 2026-08-04 실데이터 백필 화면에서 나온 문제: 변화가 없으면 전부 회색 "그대로"였다.
 *   그러면 **계속 건전한 지표와 계속 임계를 넘고 있는 지표가 같은 색**으로 보인다 —
 *   "안 나빠졌다"가 "괜찮다"로 읽혀 위험이 묻힌다. 안 변한 것도 넘고 있으면 넘고 있는 것이다.
 */
export function toneOf(delta: Delta, exceeded: boolean): Delta {
  if (delta !== 'flat') return delta;
  return exceeded ? 'worsened' : 'flat';
}

/** 배지 문구 — flat은 지금 상태를 말해준다("계속 좋음" vs "계속 넘음"). */
export function deltaWord(delta: Delta, exceeded: boolean): string {
  if (delta === 'improved') return '나아짐';
  if (delta === 'worsened') return '나빠짐';
  if (delta === 'flat') return exceeded ? '계속 넘음' : '계속 좋음';
  return '아직';
}

export function deltaSoft(d: Delta): string {
  if (d === 'improved') return 'var(--cs-income-soft)';
  if (d === 'worsened') return 'var(--cs-alert-soft)';
  return 'var(--cs-surface-2)';
}

/** 지표 1종의 회차별 시계열. */
export type MetricSeries = {
  key: string;
  label: string;
  direction: 'lower' | 'higher';
  threshold: number;
  /** 회차순(과거→최근). null은 **그대로 둔다** — 보간·0치환 금지(§5 오독 방지 ③). */
  points: (number | null)[];
  /** 측정된 값 중 가장 오래된 것 / 가장 최근 것. 없으면 null. */
  first: number | null;
  last: number | null;
  delta: Delta;
  /** 최근 회차의 초과 여부(= 지금 문제인가). */
  exceeded: boolean;
  /** 최근 회차의 표본 정보 — "표본 3/5" 안내용. */
  sample?: { have: number; need: number };
  /** 값이 실제로 측정된 회차 수. 0이면 아직 못 잰 지표, 1이면 추이를 그릴 수 없다. */
  measuredCount: number;
  /**
   * 회차별 "비어 있는 이유가 표본 부족인가". points와 같은 길이.
   * 🔴 2026-09-28: 빈 점을 전부 "표본↓"로 그렸는데, 지표 정의가 바뀌어 옛 회차를 비운 경우(물타기 v2)나
   *   지표가 그 회차에 아예 없던 경우까지 "표본이 모자라다"로 보였다. 표본 정보가 실제로 있을 때만 true.
   */
  pointSampleShort: boolean[];
};

/**
 * 회차 배열(과거→최근) → 지표별 시계열.
 * 지표 순서·구성은 **가장 최근 회차** 기준이다 — 9종이 다 차 있는 쪽이 최신이므로.
 * 옛 회차에 없던 지표는 그 자리에 null이 들어간다(측정 불가와 같은 취급 = 안 그린다).
 */
export function buildSeries(rounds: TrendRound[]): MetricSeries[] {
  if (!rounds.length) return [];
  const latest = rounds[rounds.length - 1];
  // 지표 목록은 최신 회차를 정본으로, 옛 회차에만 있는 key도 빠뜨리지 않게 뒤에 덧붙인다.
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const m of latest.metrics || []) { if (!seen.has(m.key)) { seen.add(m.key); keys.push(m.key); } }
  for (const r of rounds) {
    for (const m of r.metrics || []) { if (!seen.has(m.key)) { seen.add(m.key); keys.push(m.key); } }
  }

  return keys.map((key) => {
    const byRound = rounds.map((r) => (r.metrics || []).find((m) => m.key === key) || null);
    const points = byRound.map((m) => (m && m.value != null ? m.value : null));
    const measured = points.filter((v): v is number => v != null);
    const first = measured.length ? measured[0] : null;
    const last = measured.length ? measured[measured.length - 1] : null;
    // 라벨·direction·threshold는 값이 있는 최신 회차 것을 쓴다(임계 튜닝이 바뀌어도 현행 기준으로 읽힌다).
    const ref = [...byRound].reverse().find((m) => m != null) || null;
    const latestMetric = byRound[byRound.length - 1];
    return {
      key,
      label: ref?.label || key,
      direction: ref?.direction || 'lower',
      threshold: ref?.threshold ?? 0,
      points,
      first,
      last,
      // 추이는 **측정된 회차가 2개 이상**일 때만 판정한다. 1개뿐이면 비교 대상이 없다.
      delta: measured.length >= 2 ? judgeDelta(first, last, ref?.direction || 'lower') : 'unknown',
      exceeded: !!latestMetric?.exceeded,
      ...(latestMetric?.sample ? { sample: latestMetric.sample } : {}),
      measuredCount: measured.length,
      pointSampleShort: byRound.map((m) => !!(m && m.value == null && m.sample && m.sample.have < m.sample.need)),
    };
  });
}

/**
 * 비교 기준값 라벨 — "첫 회차 X".
 * 🔴 2026-09-28: 첫 회차가 비어 있으면 "첫 회차 —"와 "나아짐" 배지가 나란히 떠서 서로 모순이었다
 *   (판정은 **처음 측정된 회차**와 비교한다). 첫 회차가 비었으면 그 회차 번호를 밝힌다: "4회차 65%".
 */
export function baselineLabel(s: Pick<MetricSeries, 'key' | 'points'>, fmt: (key: string, v: number | null) => string): string {
  const idx = s.points.findIndex((v) => v != null);
  if (idx <= 0) return `첫 회차 ${fmt(s.key, s.points[0] ?? null)}`;
  return `${idx + 1}회차 ${fmt(s.key, s.points[idx])}`;
}

/**
 * 스파크라인용 세그먼트 — null에서 **선을 끊는다**.
 * 이어 그리면 측정 못 한 구간을 지나온 것처럼 보인다(§5 오독 방지 ③).
 * 반환: 연속 구간별 [{i: 회차 인덱스, v: 값}] 배열.
 */
export function segments(points: (number | null)[]): { i: number; v: number }[][] {
  const out: { i: number; v: number }[][] = [];
  let cur: { i: number; v: number }[] = [];
  points.forEach((v, i) => {
    if (v == null) { if (cur.length) { out.push(cur); cur = []; } return; }
    cur.push({ i, v });
  });
  if (cur.length) out.push(cur);
  return out;
}

/**
 * 최근 회차를 측정하지 못했는가 — 표시값이 **지금 값이 아니라 과거 값**임을 알리는 판정.
 *
 * 🔴 2026-08-04 e2e 육안에서 나온 문제: fomo_chase는 중간 회차에만 값이 있었는데
 *   화면은 "45%"만 크게 보여줬다. 유저는 그걸 **현재 상태**로 읽는다.
 *   측정값 자체는 진짜지만 시점이 다르다 → 색을 죽이고 표본 안내를 함께 띄운다.
 */
export function isLastStale(points: (number | null)[]): boolean {
  if (!points.length) return false;
  if (points[points.length - 1] != null) return false;
  return points.some((v) => v != null); // 값이 하나도 없으면 stale이 아니라 그냥 미측정이다
}

/**
 * 추이를 못 그리는 이유 — 그래프 자리가 **설명 없이 비는 것**을 막는다.
 *
 * 🔴 2026-08-05 라이브 화면에서 나온 문제: `concentration`은 백필 회차엔 값이 없고(그 시점 보유
 *   상태 기록이 없다) 실측 회차에만 있어서 측정 1회였다. 그래서 값만 회색으로 떠 있고 그래프·배지가
 *   둘 다 없는데 **왜 다른지 설명이 없었다** — 유저는 "이것만 왜 이러지"에서 멈춘다.
 * 표본 부족(`sample`)은 SampleNote가 이미 말해주므로 여기서는 그 외의 사유만 돌려준다.
 */
export function noTrendNote(s: Pick<MetricSeries, 'measuredCount' | 'sample'>): string | null {
  if (s.sample) return null;          // "표본 3/5"가 이미 사유를 말한다
  if (s.measuredCount === 1) return '이번 회차부터';
  return null;                        // 0회는 '아직 측정하지 못한 지표' 섹션이 받는다
}

/**
 * 스파크라인 y 정규화 범위. 지표마다 단위가 달라(일수·건수·%) 자기 범위로 정규화한다.
 * ⚠ 임계선을 함께 그려야 "지금 넘었나"를 읽을 수 있으므로 threshold도 범위에 포함한다.
 * 값이 하나뿐이거나 모두 같으면 min===max가 되어 0으로 나눌 수 없다 → 폭을 벌린다.
 */
export function normRange(points: (number | null)[], threshold: number): { min: number; max: number } {
  const vals = points.filter((v): v is number => v != null);
  const pool = Number.isFinite(threshold) ? [...vals, threshold] : vals;
  if (!pool.length) return { min: 0, max: 1 };
  let min = Math.min(...pool);
  let max = Math.max(...pool);
  if (max === min) { const pad = Math.abs(max) * 0.2 || 1; min -= pad; max += pad; }
  return { min, max };
}

/** 게이지 채움 비율(0~1.2). 임계 대비 현재값. 측정 불가면 null. */
export function gaugeRatio(value: number | null, threshold: number): number | null {
  if (value == null || !Number.isFinite(threshold) || threshold === 0) return null;
  return Math.max(0, Math.min(1.2, value / threshold));
}

/** 회차 라벨 — "7/24" (모바일 폭에 맞춰 연도 생략). */
export function roundLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

/**
 * 거래 건수 요약 — §5 오독 방지 ①의 본체.
 * **매매를 줄이기만 해도 과매매·물타기는 저절로 사라진다.** 건수를 함께 보여주지 않으면
 * 이 화면은 "습관이 바뀐 것"과 "그냥 덜 한 것"을 구분하지 못한다 = 거짓말을 하게 된다.
 * null(옛 회차라 건수 미기록)이면 문구를 만들지 않는다 — 모르는 걸 아는 척하지 않는다.
 */
export function tradeCountNote(rounds: TrendRound[]): string | null {
  const counts = rounds.map((r) => r.tradeCount).filter((c): c is number => c != null);
  if (!counts.length) return null;
  if (counts.length === 1) return `체결 ${counts[0]}건 기준`;
  const first = counts[0];
  const last = counts[counts.length - 1];
  if (first === last) return `체결 ${last}건 (첫 회차와 같음)`;
  return `체결 ${first}건 → ${last}건`;
}

/**
 * 사후 재구성 안내 — 백필한 회차가 섞여 있으면 반드시 말한다.
 *
 * 백필은 `sim_trades` 원본을 회차 시각으로 잘라(`tradesAsOf`) 다시 측정한 값이다. 값 자체는 정확하지만
 * **그때 화면이 보여준 값은 아니고**, 그 시점 보유 상태(포지션·현금)는 기록이 없어 몰빵 비중이 빠진다.
 * 이걸 숨기면 "복기할 때마다 이 지표를 재고 있었다"는 잘못된 인상을 준다.
 */
export function backfillNote(rounds: TrendRound[]): string | null {
  const n = rounds.filter((r) => r.backfilled).length;
  if (!n) return null;
  if (n === rounds.length) {
    return `${n}회차 모두 지난 매매 기록으로 다시 계산한 값이에요 · 그때 보유 상태는 남아 있지 않아 몰빵 비중은 빠졌어요`;
  }
  return `지난 ${n}회차는 매매 기록으로 다시 계산했어요 · 이후 회차는 복기 당시 측정값이에요`;
}

/**
 * 거래 건수가 줄었는데 지표가 개선된 경우의 **주의 문구**.
 * 이걸 안 붙이면 "덜 했을 뿐"을 "나아졌다"로 읽게 된다.
 */
export function volumeCaveat(rounds: TrendRound[]): string | null {
  const counts = rounds.map((r) => r.tradeCount).filter((c): c is number => c != null);
  if (counts.length < 2) return null;
  const first = counts[0];
  const last = counts[counts.length - 1];
  if (first <= 0 || last >= first) return null;
  const drop = Math.round(((first - last) / first) * 100);
  if (drop < 30) return null; // 소폭 감소는 굳이 의심하게 만들지 않는다
  return `매매 자체가 ${drop}% 줄었어요 — 습관이 바뀐 건지, 덜 한 건지는 함께 봐야 해요`;
}

/** 정렬을 유지하며 개선 → 기준 초과 → 측정된 지표 순으로 처음 보여준다. */
export function defaultFocusKey(sorted: MetricSeries[], shape: TrendShape): string | null {
  if (shape === 'empty') return null;
  const measured = sorted.filter((s) => s.measuredCount > 0);
  return (measured.find((s) => s.delta === 'improved')
    ?? measured.find((s) => s.exceeded)
    ?? measured[0])?.key ?? null;
}

/** 임계가 포함된 기존 범위 양쪽에 각각 15% 여백을 더한다. */
export function bigChartRange(points: (number | null)[], threshold: number): { min: number; max: number } {
  const { min, max } = normRange(points, threshold);
  const pad = (max - min) * 0.15;
  return { min: min - pad, max: max + pad };
}

/** 방향과 기준의 단위는 실제 값 표기와 동일하게 유지한다. */
export function directionNote(s: Pick<MetricSeries, 'key' | 'threshold' | 'direction'>): string {
  return `${s.direction === 'higher' ? '높을수록' : '낮을수록'} 건전 · 기준 ${fmtValue(s.key, s.threshold)}`;
}
